import { DirectionalHint } from "@fluentui/react/lib/Callout";
import type { IKeytipProps } from "@fluentui/react/lib/Keytip";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import { getBoundingWindowWithPaddingFabric } from "@1js/window-util";
import type { AppKeytipProps } from "./AppKeytip.types";
import { getDefaultKeytipProps as getDefaultKeytipPropsV9 } from "./Next";
import type { KeytipProps } from "@fluentui-contrib/react-keytips";

const tabOffset = 24; // Tab header keytips should be positioned 24px from the top
const fileOffset = 26; // File keytips should be positioned 26px from the top

let v9KeytipEnabled = false;
export const setKeytipEnabled = (keytipEnabled?: boolean) => {
  v9KeytipEnabled = keytipEnabled || false;
};

export const getDefaultKeytipProps = (
  keytipProps: AppKeytipProps,
  offset: number,
  onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void,
  directionalHint: DirectionalHint = DirectionalHint.topCenter
): AppKeytipProps => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (v9KeytipEnabled) {
    return getDefaultKeytipPropsV9(
      keytipProps as KeytipProps,
      offset,
      onExecute,
      directionalHint
    );
  }
  const keytipdata = keytipProps as IKeytipProps;
  return {
    onReturn: defaultOnReturn,
    ...keytipdata,
    onExecute,
    calloutProps: {
      coverTarget: true,
      directionalHint,
      gapSpace: offset,
      ...keytipdata.calloutProps,
      bounds: getBoundingWindowWithPaddingFabric,
    },
  };
};

export const defaultOnReturn = (executeTarget: HTMLElement) =>
  executeTarget && executeTarget.focus();

export const deriveTabKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): AppKeytipProps => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (v9KeytipEnabled) {
      return keytipProps
        ? getDefaultKeytipPropsV9(
            keytipProps as KeytipProps,
            tabOffset,
            onExecute
          )
        : undefined;
    }
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, tabOffset, onExecute)
      : undefined;
  }
);

export const deriveFileKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): AppKeytipProps => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (v9KeytipEnabled) {
      return keytipProps
        ? getDefaultKeytipPropsV9(
            keytipProps as KeytipProps,
            fileOffset,
            onExecute
          )
        : undefined;
    }
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, fileOffset, onExecute)
      : undefined;
  }
);

export const deriveInputKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): AppKeytipProps => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (v9KeytipEnabled) {
      return keytipProps
        ? getDefaultKeytipPropsV9(keytipProps as KeytipProps, 26, onExecute)
        : undefined;
    }
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, 26, onExecute)
      : undefined;
  }
);

const itemInMenuOffset = { x: 19, y: 17 }; // When in a menu, keytip should be positioned 19px from the left and 17px from the top
export const deriveMenuKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): AppKeytipProps => {
    if (keytipProps) {
      return {
        offset: itemInMenuOffset,
        ...keytipProps,
        onExecute,
        onReturn: defaultOnReturn,
        calloutProps: {
          gapSpace: 0,
          ...(keytipProps as IKeytipProps).calloutProps,
          bounds: getBoundingWindowWithPaddingFabric,
        },
      } as IKeytipProps;
    }
    return undefined;
  }
);

const ribbonButtonOffset = 24; // Ribbon item keytips should be positioned 24px from the top
export const deriveButtonKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void
  ): AppKeytipProps => {
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, ribbonButtonOffset, onExecute)
      : undefined;
  }
);

const ribbonPromptInputOffset = 32; // Ribbon item keytips should be positioned 32px from the top

export const derivePromptInputKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void
  ): AppKeytipProps => {
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, ribbonPromptInputOffset, onExecute)
      : undefined;
  }
);

const tabbedNavigationButtonOffset = { x: 29, y: 7 }; // When in a TabbedPanelMenu, keytip should be positioned 29px from the left and 7px from the top

export const deriveTabbedPanelMenuKeytip = memoizeFunction(
  (
    keytipProps?: AppKeytipProps,
    onExecute?: (executeTarget: HTMLElement) => void
  ): AppKeytipProps => {
    if (keytipProps) {
      return {
        offset: tabbedNavigationButtonOffset,
        ...keytipProps,
        onExecute: onExecute
          ? (_ev, target) => {
              // If target is available, pass it as the first argument, otherwise fallback to null
              onExecute(target as HTMLElement);
            }
          : undefined,
        onReturn: defaultOnReturn,
        calloutProps: {
          gapSpace: 0,
          ...(keytipProps as IKeytipProps).calloutProps,
          bounds: getBoundingWindowWithPaddingFabric,
        },
      } as IKeytipProps;
    }
    return undefined;
  }
);
